import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Calendar, Users, MapPin, Globe, X, ExternalLink, Clock, CheckCircle2, BedDouble, ShieldCheck, Video } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const sym = (c) => (c === 'inr' ? '₹' : '$');
const statusColor = {
  confirmed: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700', refunded: 'bg-mahogany/10 text-mahogany',
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };

const MyBookings = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);       // booking being viewed
  const [program, setProgram] = useState(null);     // fetched program for detail
  const [progLoading, setProgLoading] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [waiver, setWaiver] = useState(null);       // { waiver_text, signers }
  const [signOpen, setSignOpen] = useState(null);   // signer index (or 'solo') being signed
  const [signForm, setSignForm] = useState({});
  const [signing, setSigning] = useState(false);

  const fetchMine = async () => {
    setLoading(true);
    try { const res = await api.get('/bookings/mine'); setItems(res.data || []); }
    catch (e) { console.error('Failed to load bookings:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchMine(); }, []);

  const openDetail = async (b) => {
    setDetail(b); setProgram(null); setProgLoading(true); setWaiver(null); setSignOpen(null);
    try { const res = await api.get(`/programs/slug/${b.program_slug}`); setProgram(res.data); }
    catch (e) { console.error('Program may be unpublished:', e); } finally { setProgLoading(false); }
    try { const w = await api.get(`/bookings/${b.id}/waiver`); setWaiver(w.data); }
    catch (e) { console.error('Waiver load failed:', e); }
  };

  const submitSign = async (signer) => {
    if (!signForm.signature?.trim()) { toast({ title: 'Please type your full name to sign' }); return; }
    setSigning(true);
    try {
      await api.post(`/bookings/${detail.id}/waiver`, {
        guest_index: signer.index,
        signature: signForm.signature,
        media_optout: !!signForm.media_optout,
        medical_notes: signForm.medical_notes || '',
        emergency_contact_name: signForm.emergency_contact_name || '',
        emergency_contact_phone: signForm.emergency_contact_phone || '',
      });
      toast({ title: 'Waiver signed', description: signer.name });
      setSignOpen(null); setSignForm({});
      const w = await api.get(`/bookings/${detail.id}/waiver`); setWaiver(w.data);
      fetchMine();
    } catch (e) { toast({ title: 'Could not sign', description: e?.response?.data?.detail }); }
    finally { setSigning(false); }
  };

  const cancel = async (b) => {
    if (!window.confirm('Cancel this booking? Paid bookings will be flagged for a refund.')) return;
    setCancelling(b.id);
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast({ title: 'Booking cancelled', description: b.payment_status === 'paid' ? 'A refund has been requested.' : undefined });
      setDetail(null);
      fetchMine();
    } catch (e) { toast({ title: 'Could not cancel', description: e?.response?.data?.detail }); }
    finally { setCancelling(null); }
  };

  const canCancel = (b) => ['pending', 'confirmed'].includes(b.status);

  return (
    <div className="space-y-6">
      <div><div className="eyebrow mb-2">My Journey</div><h1 className="font-serif-display text-4xl text-burgundy">My <span className="font-italic-display text-rose-italic">Bookings</span></h1></div>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? (
          <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40">
            You have no bookings yet. <Link to="/events" className="text-mahogany underline">Browse programs</Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((b) => (
              <li key={b.id} className="p-5 rounded-2xl border border-rose/50 bg-white/70 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif-display text-xl text-burgundy">{b.program_title}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-rose-soft text-mahogany capitalize">{b.program_type}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${statusColor[b.status] || ''}`}>{b.status}</span>
                    {b.refund_status === 'requested' && <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-amber-50 text-amber-700">Refund requested</span>}
                    {b.checked_in && <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-green-100 text-green-800 inline-flex items-center gap-1" data-testid={`checkin-badge-${b.id}`}><CheckCircle2 className="w-3 h-3" /> Checked In</span>}
                    {!b.checked_in && b.guests?.length > 0 && b.guests.some(g => g.checked_in) && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 text-amber-800">{b.guests.filter(g => g.checked_in).length}/{b.guests.length} checked in</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-mahogany/70 mt-1.5">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {b.registrants} registrant{b.registrants > 1 ? 's' : ''}</span>
                    {b.num_days > 1 && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {b.num_days} days</span>}
                    {b.room && <span className="flex items-center gap-1 text-burgundy"><BedDouble className="w-3.5 h-3.5" /> Room {b.room}</span>}
                    {!b.room && b.guests?.some(g => g.room) && <span className="flex items-center gap-1 text-burgundy"><BedDouble className="w-3.5 h-3.5" /> Rooms {b.guests.filter(g => g.room).map(g => g.room).join(', ')}</span>}
                    {b.discount_amount > 0 && <span className="text-green-700">Saved {sym(b.currency)}{b.discount_amount.toFixed(2)} ({b.discount_percent}%)</span>}
                  </div>
                  {(b.online_url || b.joining_instructions) && (
                    <div className="mt-3 rounded-xl border border-rose/40 bg-rose-soft/20 p-3 text-sm" data-testid={`join-info-${b.id}`}>
                      <div className="font-medium text-burgundy inline-flex items-center gap-1.5"><Video className="w-4 h-4 text-rose-italic" /> Online joining details</div>
                      {b.online_url && <a href={b.online_url} target="_blank" rel="noreferrer" className="block mt-1 text-rose-italic underline break-all">{b.online_url}</a>}
                      {b.joining_instructions && <div className="mt-1 text-mahogany/80 whitespace-pre-wrap">{b.joining_instructions}</div>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="font-serif-display text-2xl text-burgundy">{b.payment_status === 'free' || b.total === 0 ? 'Free' : `${sym(b.currency)}${b.total.toFixed(2)}`}</div>
                    <div className="text-xs text-mahogany/60 capitalize">{b.payment_status}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => openDetail(b)} className="pill-btn-outline text-xs py-1.5">Details</button>
                    {b.is_donation && b.payment_status === 'paid' && (
                      <button onClick={() => { const w = window.open('', '_blank'); api.get(`/bookings/${b.id}/donation-receipt`, { responseType: 'blob' }).then((r) => { w.location.href = URL.createObjectURL(r.data); }).catch(() => { w?.close(); toast({ title: 'Receipt unavailable' }); }); }}
                        className="pill-btn-outline text-xs py-1.5" data-testid={`donation-receipt-${b.id}`}>Tax receipt</button>
                    )}
                    {canCancel(b) && (
                      <button onClick={() => cancel(b)} disabled={cancelling === b.id}
                        className="text-xs px-4 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center gap-1">
                        {cancelling === b.id && <Loader2 className="w-3 h-3 animate-spin" />} Cancel
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-cream rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-display text-2xl text-burgundy">{detail.program_title}</h2>
              <button onClick={() => setDetail(null)} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
            </div>

            {/* Event info */}
            <div className="space-y-2 text-sm text-burgundy/85">
              {progLoading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading event…</div>
                : program ? (
                  <>
                    {program.start_datetime && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-rose-italic" /> {fmt(program.start_datetime)}{program.end_datetime ? ` → ${fmt(program.end_datetime)}` : ''}</div>}
                    {program.type === 'class' && program.time_of_day && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-rose-italic" /> {program.time_of_day}{program.duration_minutes ? ` · ${program.duration_minutes} min` : ''}</div>}
                    <div className="flex items-center gap-2">{program.mode === 'online' ? <Globe className="w-4 h-4 text-rose-italic" /> : <MapPin className="w-4 h-4 text-rose-italic" />} {program.mode === 'online' ? 'Online' : (program.location || 'In person')}</div>
                    <Link to={`/programs/${detail.program_slug}`} className="inline-flex items-center gap-1 text-mahogany underline"><ExternalLink className="w-3.5 h-3.5" /> View event page</Link>
                  </>
                ) : <div className="text-mahogany/60">This event is no longer publicly listed.</div>}
            </div>

            {/* Check-in status */}
            {detail.checked_in && (
              <div className="mt-4 p-3 rounded-2xl bg-green-50 border border-green-200 flex items-center gap-3 text-sm" data-testid="detail-checkin-banner">
                <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0" />
                <div>
                  <div className="text-green-800 font-medium">You're checked in</div>
                  {detail.room && <div className="text-green-700 flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> Room {detail.room}</div>}
                </div>
              </div>
            )}

            {/* Per-guest rooms & check-in */}
            {detail.guests?.length > 0 && (
              <div className="mt-4 p-4 rounded-2xl bg-white/70 border border-rose/40" data-testid="detail-guests">
                <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-2">Guests</div>
                <ul className="space-y-2">
                  {detail.guests.map((g, i) => (
                    <li key={`${g.name || 'guest'}-${i}`} className="flex items-center justify-between text-sm">
                      <span className="text-burgundy">{g.name || `Guest ${i + 1}`}</span>
                      <span className="flex items-center gap-3">
                        {g.room && <span className="flex items-center gap-1 text-burgundy"><BedDouble className="w-3.5 h-3.5" /> {g.room}</span>}
                        {g.checked_in
                          ? <span className="inline-flex items-center gap-1 text-green-700 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Checked in</span>
                          : <span className="text-mahogany/50 text-xs">Not yet</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Waiver signing */}
            {waiver && (
              <div className="mt-4 p-4 rounded-2xl bg-white/70 border border-rose/40" data-testid="waiver-section">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-mahogany/70 mb-2">
                  <ShieldCheck className="w-3.5 h-3.5" /> Liability & Media Release Waiver
                </div>
                <details className="mb-3">
                  <summary className="text-sm text-mahogany underline cursor-pointer" data-testid="waiver-read">Read the full waiver</summary>
                  <div className="mt-2 max-h-56 overflow-y-auto whitespace-pre-line text-xs text-mahogany/80 bg-cream-2/50 rounded-xl p-3 leading-relaxed">
                    {waiver.waiver_text}
                  </div>
                </details>
                <ul className="space-y-2">
                  {waiver.signers.map((sgn) => (
                    <li key={sgn.index ?? 'solo'} className="rounded-xl border border-rose/40 bg-white/60 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-burgundy">{sgn.name}</span>
                        {sgn.signed ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Signed{sgn.signed_by === 'admin' ? ' (staff)' : ''}</span>
                        ) : (
                          <button onClick={() => { setSignOpen(sgn.index ?? 'solo'); setSignForm({ signature: '' }); }}
                            data-testid={`sign-waiver-${sgn.index ?? 'solo'}`}
                            className="text-xs px-3 py-1.5 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors">Sign waiver</button>
                        )}
                      </div>
                      {(signOpen === (sgn.index ?? 'solo')) && !sgn.signed && (
                        <div className="mt-3 space-y-2" data-testid="sign-form">
                          <input value={signForm.signature || ''} onChange={(e) => setSignForm({ ...signForm, signature: e.target.value })}
                            placeholder="Type your full name to sign" data-testid="sign-signature"
                            className="w-full bg-white rounded-lg border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input value={signForm.emergency_contact_name || ''} onChange={(e) => setSignForm({ ...signForm, emergency_contact_name: e.target.value })}
                              placeholder="Emergency contact name" className="bg-white rounded-lg border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                            <input value={signForm.emergency_contact_phone || ''} onChange={(e) => setSignForm({ ...signForm, emergency_contact_phone: e.target.value })}
                              placeholder="Emergency contact phone" className="bg-white rounded-lg border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                          </div>
                          <input value={signForm.medical_notes || ''} onChange={(e) => setSignForm({ ...signForm, medical_notes: e.target.value })}
                            placeholder="Medical conditions / allergies / medications (or 'None')"
                            className="w-full bg-white rounded-lg border border-rose/50 px-3 py-2 text-sm text-burgundy focus:outline-none focus:border-mahogany" />
                          <label className="flex items-start gap-2 text-xs text-mahogany/80">
                            <input type="checkbox" checked={!!signForm.media_optout} onChange={(e) => setSignForm({ ...signForm, media_optout: e.target.checked })} className="mt-0.5" data-testid="sign-media-optout" />
                            I do NOT consent to the use of my photo/video for social media or promotional purposes.
                          </label>
                          <div className="flex justify-end gap-2">
                            <button onClick={() => setSignOpen(null)} className="text-xs px-3 py-1.5 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
                            <button onClick={() => submitSign(sgn)} disabled={signing || !signForm.signature?.trim()} data-testid="sign-submit"
                              className="text-xs px-3 py-1.5 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-1">
                              {signing && <Loader2 className="w-3.5 h-3.5 animate-spin" />} I agree & sign
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Booking breakdown */}
            <div className="mt-5 p-4 rounded-2xl bg-white/70 border border-rose/40 text-sm space-y-1.5">
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-1">Your booking</div>
              {detail.tickets?.length > 0
                ? detail.tickets.map((t) => <Row key={t.ticket_type_id} label={`${t.name} × ${t.quantity}`} value={`${sym(detail.currency)}${t.line_total.toFixed(2)}`} />)
                : <Row label="Registrants" value={detail.registrants} />}
              {detail.num_days > 1 && <Row label="Days" value={detail.num_days} />}
              {!detail.tickets?.length && detail.base_subtotal > 0 && <Row label="Program fee" value={`${sym(detail.currency)}${detail.base_subtotal.toFixed(2)}`} />}
              {detail.discount_amount > 0 && <Row label={`${detail.membership_tier} discount (${detail.discount_percent}%)`} value={`− ${sym(detail.currency)}${detail.discount_amount.toFixed(2)}`} accent />}
              {detail.addons?.map((a) => <Row key={a.addon_id} label={`${a.name} × ${a.quantity}`} value={`${sym(detail.currency)}${a.line_total.toFixed(2)}`} />)}
              <div className="pt-2 mt-1 border-t border-rose/40 flex justify-between font-serif-display text-lg text-burgundy">
                <span>Total</span><span>{detail.payment_status === 'free' || detail.total === 0 ? 'Free' : `${sym(detail.currency)}${detail.total.toFixed(2)}`}</span>
              </div>
              <div className="text-xs text-mahogany/60 capitalize pt-1">Status: {detail.status} · Payment: {detail.payment_status}</div>
            </div>

            {canCancel(detail) && (
              <button onClick={() => cancel(detail)} disabled={cancelling === detail.id}
                className="w-full mt-5 px-4 py-2.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2">
                {cancelling === detail.id && <Loader2 className="w-4 h-4 animate-spin" />} Cancel this booking
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between"><span className="text-burgundy/80">{label}</span><span className={accent ? 'text-green-700 font-medium' : 'text-burgundy'}>{value}</span></div>
);

export default MyBookings;
